import { useEffect, useRef } from 'react'
import { AlertCircle } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'

import { useConversation } from '@/features/messaging/hooks'
import type { Message } from '@/types/messaging'

function MessageBubble({ message }: { message: Message }) {
  const outbound = message.direction === 'outbound'
  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
          outbound ? 'rounded-br-sm bg-primary text-primary-foreground' : 'rounded-bl-sm bg-muted'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <div
          className={`mt-1 flex items-center gap-1 text-[10px] ${
            outbound ? 'text-primary-foreground/70' : 'text-muted-foreground'
          }`}
        >
          {message.status === 'failed' && <AlertCircle className="h-3 w-3" aria-label="failed" />}
          <span>
            {message.status === 'failed'
              ? `failed${message.error ? `: ${message.error}` : ''}`
              : new Date(message.created_at).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
          </span>
        </div>
      </div>
    </div>
  )
}

interface MessageThreadProps {
  customerId: string
  className?: string
}

export function MessageThread({ customerId, className }: MessageThreadProps) {
  const { data: messages, isLoading, isError } = useConversation(customerId)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'nearest' })
  }, [messages?.length])

  if (isLoading) {
    return (
      <div className={`flex flex-col gap-3 ${className ?? ''}`}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-2/3" />
        ))}
      </div>
    )
  }

  if (isError) {
    return <p className="text-sm text-destructive">Failed to load messages.</p>
  }

  if (!messages || messages.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No messages yet.</p>
  }

  return (
    <div className={`flex flex-col gap-2 overflow-y-auto ${className ?? ''}`}>
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
      <div ref={bottomRef} />
    </div>
  )
}
